import type { SuggestionItem } from "@digdir/designsystemet-react";

export type GroupedSuggestionGroup = SuggestionItem & {
    children: SuggestionItem[];
};

export type GroupedSuggestionConfig = {
    groups: GroupedSuggestionGroup[];
    standaloneOptions?: SuggestionItem[];
};

export function getGroupedSuggestionOptions(config: GroupedSuggestionConfig): SuggestionItem[] {
    return [
        ...config.groups.flatMap((group) => [
            { label: group.label, value: group.value },
            ...group.children.map((child) => ({ label: child.label, value: child.value })),
        ]),
        ...(config.standaloneOptions ?? []).map((option) => ({ label: option.label, value: option.value })),
    ];
}

export function getGroupedSuggestionLabelByValue(config: GroupedSuggestionConfig): Map<string, string> {
    return new Map(getGroupedSuggestionOptions(config).map((option) => [option.value, option.label]));
}

export function normalizeGroupedSelection(
    config: GroupedSuggestionConfig,
    values: SuggestionItem[],
    previousValues: SuggestionItem[] = [],
): SuggestionItem[] {
    const selectedValues = new Set(values.map((item) => item.value));
    const previousSelectedValues = new Set(previousValues.map((item) => item.value));

    config.groups.forEach((group) => {
        const childValues = group.children.map((child) => child.value);
        const hasChildren = childValues.length > 0;
        const allChildrenSelected = childValues.every((childValue) => selectedValues.has(childValue));
        const hasParentSelected = selectedValues.has(group.value);
        const hadParentSelected = previousSelectedValues.has(group.value);

        if (hasParentSelected && !hadParentSelected) {
            childValues.forEach((childValue) => {
                selectedValues.add(childValue);
            });
            return;
        }

        if (!hasParentSelected && hadParentSelected) {
            childValues.forEach((childValue) => {
                selectedValues.delete(childValue);
            });
            return;
        }

        if (hasChildren && !hasParentSelected && allChildrenSelected) {
            selectedValues.add(group.value);
            return;
        }

        if (hasChildren && hasParentSelected && hadParentSelected && !allChildrenSelected) {
            selectedValues.delete(group.value);
        }
    });

    const labelByValue = getGroupedSuggestionLabelByValue(config);

    return Array.from(selectedValues).map((value) => ({
        label: labelByValue.get(value) ?? value,
        value,
    }));
}
